import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import AuthPage from "./pages/AuthPage";
import FeedPage from "./pages/FeedPage";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import EditProfilePage from "./pages/EditProfilePage";
import UserProfilePage from "./pages/UserProfilePage";
import OnboardingPage from "./pages/OnboardingPage";
import PendingVerificationPage from "./pages/PendingVerificationPage";
import AdminPage from "./pages/AdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import SearchPage from "./pages/SearchPage";
import NearMePage from "./pages/NearMePage";
import WelcomePage from "./pages/WelcomePage";
import PollPage from "./pages/PollPage";
import DonationPage from "./pages/DonationPage";
import PinSetupPage from "./pages/PinSetupPage";
import UpdatesPage from "./pages/UpdatesPage";
import AboutPage from "./pages/AboutPage";
import FamilyTreePage from "./pages/FamilyTreePage";
import TravelFormPage from "./pages/TravelFormPage";
import KuldeviFormPage from "./pages/KuldeviFormPage";
import InstallPWA from "./components/InstallPWA";
import { NotificationProvider } from "./contexts/NotificationContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with auth
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children, allowIncomplete = false, requiresVerified = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // For routes that don't allow incomplete profiles
  if (!allowIncomplete) {
    // First check if user needs to set up PIN
    if (!user.has_pin) {
      return <Navigate to="/pin-setup" replace />;
    }
    // Then check if profile is incomplete
    if (!user.is_profile_complete) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Redirect to pending verification if not verified (only for routes that require it)
  if (requiresVerified && user.verification_status !== "verified") {
    return <Navigate to="/pending-verification" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (_error) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (userData, token, isNewUser = false) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("isNewUser", isNewUser ? "true" : "false");
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  // Helper to determine where to redirect after login
  const getRedirectPath = (userData) => {
    // First check if user needs to set up PIN
    if (userData?.has_pin === false) {
      return "/pin-setup";
    }
    // Rejected users should go to pending-verification to see Keep/Remove options
    if (userData?.verification_status === "rejected") {
      return "/pending-verification";
    }
    if (!userData?.is_profile_complete) {
      return "/onboarding";
    }
    if (userData?.verification_status !== "verified") {
      return "/pending-verification";
    }
    return "/welcome";
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      <NotificationProvider>
        <div className="App">
          <Toaster position="top-center" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={
                user ? (
                  <Navigate to={getRedirectPath(user)} replace />
                ) : (
                  <AuthPage />
                )
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute allowIncomplete>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              <Route path="/pin-setup" element={
                <ProtectedRoute allowIncomplete>
                  <PinSetupPage />
                </ProtectedRoute>
              } />
              <Route path="/pending-verification" element={
                <ProtectedRoute allowIncomplete>
                  <PendingVerificationPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={<ProtectedRoute allowIncomplete><AdminPage /></ProtectedRoute>} />
              <Route path="/admin-login" element={<AdminLoginPage />} />
              <Route path="/admin-panel" element={<AdminPanelPage />} />
              <Route path="/search" element={<ProtectedRoute requiresVerified><SearchPage /></ProtectedRoute>} />
              <Route path="/near-me" element={<ProtectedRoute requiresVerified><NearMePage /></ProtectedRoute>} />
              <Route path="/welcome" element={<ProtectedRoute requiresVerified><WelcomePage /></ProtectedRoute>} />
              <Route path="/polls" element={<ProtectedRoute requiresVerified><PollPage /></ProtectedRoute>} />
              <Route path="/donation" element={<ProtectedRoute requiresVerified><DonationPage /></ProtectedRoute>} />
              <Route path="/updates" element={<ProtectedRoute requiresVerified><UpdatesPage /></ProtectedRoute>} />
              <Route path="/about" element={<ProtectedRoute requiresVerified><AboutPage /></ProtectedRoute>} />
              <Route path="/family-tree" element={<ProtectedRoute requiresVerified><FamilyTreePage /></ProtectedRoute>} />
              <Route path="/feed" element={<ProtectedRoute requiresVerified><FeedPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute requiresVerified><ProfilePage /></ProtectedRoute>} />
              <Route path="/profile/edit" element={<ProtectedRoute requiresVerified><EditProfilePage /></ProtectedRoute>} />
              <Route path="/user/:userId" element={<ProtectedRoute requiresVerified><UserProfilePage /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute requiresVerified><MessagesPage /></ProtectedRoute>} />
              <Route path="/messages/:conversationId" element={<ProtectedRoute requiresVerified><MessagesPage /></ProtectedRoute>} />
              <Route path="/travel-form" element={<TravelFormPage />} />
              <Route path="/kuldevi-form" element={<KuldeviFormPage />} />
            </Routes>
            
            {/* PWA Install Prompt */}
            <InstallPWA />
          </BrowserRouter>
        </div>
      </NotificationProvider>
    </AuthContext.Provider>
  );
}

export default App;
